<template>
  <div class="pr-15">
    <div class="rows mb-5">
      <div class="cols align-items-start pr-5 col-width">
        <label for="status">
          {{ $t('task.status') }}
        </label>
        <ChooseTaskStatus id="status"
                          v-model="statusFilter"
                          :add-all-option="true" />
      </div>
      <div class="cols align-items-start pr-5 col-width">
        <label for="dateFrom">{{ $t('reports.events.date-from') }}</label>
        <Datepicker id="dateFrom"
                    v-model="dateFromFilter"
                    :format="$t('settings.generic.date-format')"
                    :locale="locale"
                    :max-date="dateToFilter"
                    :time-picker="false"
                    :enable-time-picker="false"
                    clearable
                    auto-apply/>
      </div>
      <div class="cols align-items-start pr-5 col-width">
        <label for="dateTo">{{ $t('reports.events.date-to') }}</label>
        <Datepicker id="dateTo"
                    v-model="dateToFilter"
                    :format="$t('settings.generic.date-format')"
                    :locale="locale"
                    :min-date="dateFromFilter"
                    :time-picker="false"
                    :enable-time-picker="false"
                    clearable
                    auto-apply/>
      </div>
      <div class="cols align-items-start pr-5 col-width">
        <label for="vehicleFilter">{{ $t('task.vehicle') }}</label>
        <ChooseVehicles id="vehicleFilter"
                        :preselectAll="false"
                        :single-select="true"
                        :add-all-option="true"
                        v-model="vehicleFilter" />
      </div>
      <div class="cols align-items-start pr-5 col-width">
        <label for="driverFilter">
          {{ $t('task.driver') }}
        </label>
        <ChooseDriver id="driverFilter"
                      v-model="driverFilter"
                      :add-all-option="true">
        </ChooseDriver>
      </div>
      <div class="cols align-items-start pr-5 col-width">
        <label for="tagsFilter">
          {{ $t('task.tag-type') }}
        </label>
        <ChooseTaskTagType id="tagsFilter"
                           v-model="tagsFilter"
                           :include-oazis-integration="isOazisCompany" />
      </div>
      <div class="rows align-items-end pr-5 all-tasks-search-action">
        <button type="button" @click="reloadTasks">
          {{ $t('reports.events.search') }}
        </button>
      </div>
    </div>
    <Accordion class="mb-10 task-accordion" v-for="task in this.filteredTasksByToken" v-bind:key="task.id" :id="task.id" ref="tasksRef"
               :metadata="task" :expanded="false" @expanded="loadTask(task.id)">
      <template #title>
        <div class="cols mb-10 task-title">
          <div class="task-tags" v-if="task.tagTypes.length > 0">
            <TaskTag v-for="tagType in task.tagTypes"
                     v-bind:key="tagType"
                     :type="tagType" />
          </div>
          <div class="rows task-title-content justify-space-between">
            <div class="cols pr-10">
              <span class="readonly-label">
                {{ $t('task.name') }}
              </span>
              <b>{{task.name}}</b>
            </div>
            <div class="cols pr-10">
              <span class="readonly-label">
                {{ $t('task.status') }}
              </span>
              <b>{{task.statusName}}</b>
            </div>
            <div class="cols pr-10">
              <span class="readonly-label">
                {{ $t('task.start-date') }}
              </span>
              <b>{{task.startDateFormatted}}</b>
            </div>
            <div class="cols pr-10">
              <span class="readonly-label">
                {{ $t('task.schedule-date') }}
              </span>
              <b>{{task.scheduleDateFormatted}}</b>
            </div>
            <div class="cols pr-10">
              <span class="readonly-label">
                {{ $t('task.vehicle') }}
              </span>
              <b>{{task.vehicleName}}</b>
            </div>
            <div class="cols pr-10">
              <span class="readonly-label">
                {{ $t('task.driver') }}
              </span>
              <b>{{task.driverName}}</b>
            </div>
            <div class="rows justify-end align-items-center">
              <img
                  v-if="task.automaticallyGenerated"
                  src="@/assets/images/tasks/auto-generated.png"
                  class="auto-generated-task-icon"
                  :alt="$t('task.auto-generated')"
                  :title="$t('task.auto-generated')"
              />
              <Dropdown>
                <template #trigger>
                  <button class="transparent font-size-24">
                    <i class="bi bi-three-dots-vertical"></i>
                  </button>
                </template>

                <template #default="{ close }">
                  <DropdownContent>
                    <div class="cols">
                      <button type="button" @click="close(); sendTaskLink(task)">
                        {{ $t('task.send-link') }}
                      </button>
                      <button type="button" @click="close(); copyTask(task)" class="mt-5">
                        {{ $t('generic.copy') }}
                      </button>
                      <button type="button" @click="close(); loadTaskRoutes(task)" class="mt-5" v-if="hasConfiguredVehicle(task)">
                        {{ $t('task.load-routes') }}
                      </button>
                      <button type="button" @click="close(); detectTaskVehicle(task)" class="mt-5" v-if="!hasConfiguredVehicle(task)">
                        {{ $t('task.load-vehicle') }}
                      </button>
                      <button type="button" @click="close(); validateTask(task)" class="mt-5" v-if="!isTaskCompleted(task)">
                        {{ $t('task.validate') }}
                      </button>
                      <button type="button" @click="close(); deleteTask(task)" class="mt-5" v-if="!isTaskFinished(task)">
                        {{ $t('generic.delete') }}
                      </button>
                    </div>
                  </DropdownContent>
                </template>
              </Dropdown>
            </div>
          </div>
        </div>
      </template>
      <template #content>
        <EditTaskPage :data="task"
                      :ref="el => setEditTaskPageRef(task.id, el)"
                      v-if="task.isAdditionalDataLoaded"
                      @task-saved="onTaskSaved">
        </EditTaskPage>
      </template>
    </Accordion>
  </div>
</template>

<script>

import Accordion from "@/components/pages/partials/Accordion.vue";
import {mapGetters, mapState} from "vuex";
import EditTaskPage from "@/components/pages/task-planning/EditTaskPage.vue";
import {dataAggregator, dataManipulator, entityDataManager} from "@/assets/js/data-manipulation";
import {TaskStatus} from "@/assets/js/enums/TaskStatus.ts";
import {TaskTagType} from "@/assets/js/enums/TaskTagType.ts";
import {actions, mutations} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";
import { Dropdown, DropdownContent } from 'v-dropdown'
import {popups} from "@/assets/js/popups";
import ChooseTaskStatus from "@/components/pages/partials/ChooseTaskStatus.vue";
import Datepicker from "@vuepic/vue-datepicker";
import locale from "@/assets/js/locale";
import ChooseVehicles from "@/components/pages/partials/ChooseVehicles.vue";
import ChooseDriver from "@/components/pages/partials/ChooseDriver.vue";
import ChooseTaskTagType from "@/components/pages/partials/ChooseTaskTagType.vue";
import TaskTag from "@/components/pages/partials/TaskTag.vue";

export default {
  name: "AllTasksPage",
  components: {
    TaskTag,
    ChooseTaskTagType,
    ChooseDriver,
    ChooseVehicles, Datepicker, ChooseTaskStatus, EditTaskPage, Accordion, Dropdown, DropdownContent},
  props: {
    searchToken: String
  },
  data() {
    return {
      locale: locale.getLocale(),
      loadedTaskIds: [],
      editTaskPageRefs: {},
      selectedTaskId: this.$route.query?.id ?? null,
    }
  },
  computed: {
    ...mapState(['tasks', 'drivers', 'vehicles']),
    ...mapGetters(['isOazisCompany']),
    statusFilter: {
      get() {
        return this.$store.state.tasksFilterStatus;
      },
      set(value) {
        this.$store.commit(mutations.SET_TASKS_FILTER_STATUS, value);
      }
    },
    dateFromFilter: {
      get() {
        return this.$store.state.tasksFilterDateFrom;
      },
      set(value) {
        this.$store.commit(mutations.SET_TASKS_FILTER_DATE_FROM, value);
      }
    },
    dateToFilter: {
      get() {
        return this.$store.state.tasksFilterDateTo;
      },
      set(value) {
        this.$store.commit(mutations.SET_TASKS_FILTER_DATE_TO, value);
      }
    },
    driverFilter: {
      get() {
        return this.$store.state.tasksFilterDriver;
      },
      set(value) {
        this.$store.commit(mutations.SET_TASKS_FILTER_DRIVER, value);
      }
    },
    vehicleFilter: {
      get() {
        return this.$store.state.tasksFilterVehicle;
      },
      set(value) {
        this.$store.commit(mutations.SET_TASKS_FILTER_VEHICLE, value);
      }
    },
    tagsFilter: {
      get() {
        return this.$store.state.tasksFilterTags;
      },
      set(value) {
        this.$store.commit(mutations.SET_TASKS_FILTER_TAGS, value);
      }
    },
    mappedTasks() {
      return this.tasks.map(x => {
        return {
          ...x,
          ...{
            statusName: this.getStatusName(x.status),
            startDateFormatted: this.formatDate(x.startDate),
            scheduleDateFormatted: this.formatDate(x.scheduleDate),
            driverName: this.getDriverNameById(x.driverId),
            vehicleName: this.getVehicleNameById(x.vehicleId),
            tagTypes: this.getTaskTagTypes(x.tags),
            isAdditionalDataLoaded: this.isTaskLoaded(x.id)
          }}
      })
    },
    filteredTasksByToken() {
      return dataAggregator.filterListByToken(this.mappedTasks,
          ['name', 'description', 'statusName', 'driverName', 'vehicleName'],
          this.searchToken);
    },
    hasSelectedTask() {
      return this.selectedTaskId != null;
    }
  },
  mounted() {
    if (this.tasks.length > 1 && this.hasSelectedTask) {
      this.expandTaskBySelectedTaskId();
    }
  },
  watch: {
    tasks() {
      setTimeout(() => {
        if (this.tasks.length > 1 && this.hasSelectedTask) {
          this.expandTaskBySelectedTaskId();
        }
      }, 100);
    }
  },
  methods: {
    reloadTasks() {
      return this.$store.dispatch(actions.GET_TASKS)
          .catch(e => notification.error(this.$t(e.message)));
    },
    onTaskSaved() {
      this.reloadTasks();
    },
    deleteTask(task) {
      popups.yesNoConfirmation(this.$swal, this.$t('generic.delete.confirm'))
          .then(async result => {
            if (!result.isConfirmed) {
              return;
            }

            const payload = {id: task.id};
            await this.$store.dispatch(actions.DELETE_TASK, payload)
                .then(() => {
                  this.$store.commit(mutations.REMOVE_TASK, payload)
                  notification.success(this.$t('generic.delete.success'));
                })
                .catch(e => notification.error(this.$t(e.message)));
          });
    },
    sendTaskLink(task) {
      this.$store.dispatch(actions.SEND_TASK_LINK, task)
          .catch(e => notification.error(this.$t(e.message)));
    },
    copyTask(task) {
      this.$router.push({path: '/task-planner/new-task', query: {"copy-from": task.id}});
    },
    detectTaskVehicle(task) {
      this.$store.dispatch(actions.DETECT_TASK_VEHICLE, task)
          .then(() => this.reloadTasks())
          .catch(e => notification.error(this.$t(e.message)));
    },
    validateTask(task) {
      if (!this.hasConfiguredVehicle(task)) {
        popups.information(this.$swal, this.$t('task.vehicle-required'));
        return;
      }

      this.$store.dispatch(actions.VALIDATE_TASK, task)
          .catch(e => notification.error(this.$t(e.message)));
    },
    async loadTaskRoutes(task) {
      const isLoaded = this.isTaskLoaded(task.id);
      const shouldForceLoadWhenNotLoaded = !isLoaded &&
          !this.isTaskFinished(task) &&
          !entityDataManager.isTaskInProgress(task);
      const accordion = this.getTaskAccordionById(task.id);


      if (accordion && !accordion.isExpanded) {
        accordion.expand();
      }

      await this.$nextTick();

      if (isLoaded || shouldForceLoadWhenNotLoaded) {
        setTimeout(() => this.editTaskPageRefs[task.id]?.loadTripsForMapForce(), 100);
      }
    },
    setEditTaskPageRef(taskId, componentRef) {
      if (componentRef) {
        this.editTaskPageRefs[taskId] = componentRef;
        return;
      }

      delete this.editTaskPageRefs[taskId];
    },
    getStatusName(status) {
      const statusObject = TaskStatus.get(status);
      return statusObject !== null ? this.$t(statusObject?.translationKey) : '';
    },
    formatDate(date) {
      if (!date) {
        return '-';
      }

      const data = dataManipulator.iso8601DateStringToLocalMoment(date);
      return data !== null ? data.format(this.$t('settings.generic.date-time-format-moment')) : ''
    },
    getDriverNameById(driverId) {
      if (!driverId) {
        return '-';
      }
      return entityDataManager.getById(this.drivers, driverId)?.name ?? '-';
    },
    getVehicleNameById(vehicleId) {
      if (!vehicleId) {
        return '-';
      }

      return entityDataManager.getById(this.vehicles, vehicleId)?.nickname ?? '-';
    },
    getTaskTagTypes(tags) {
      return Array.isArray(tags)
          ? tags.map(tag => Number(tag)).filter(tag => Number.isInteger(tag) && TaskTagType.get(tag))
          : [];
    },
    hasConfiguredVehicle(task) {
      return Boolean(task?.vehicleId);
    },
    loadTask(taskId) {
      this.loadedTaskIds.push(taskId);
    },
    expandTaskBySelectedTaskId() {
      this.expandTask(this.selectedTaskId);
      setTimeout(() => this.scrollToTaskId(this.selectedTaskId), 100);
    },
    expandTask(taskId) {
      const taskAccordion = this.getTaskAccordionById(taskId);
      taskAccordion?.expand();
    },
    isTaskLoaded(taskId) {
      return this.loadedTaskIds.some(x => x === taskId);
    },
    getTaskAccordionById(id) {
      return this.$refs.tasksRef?.find(x => x.metadata?.id?.toString() === id.toString());
    },
    scrollToTaskId(taskId) {
      const row = document.getElementById(taskId);
      row?.scrollIntoView({
        behavior: 'instant',
        block: 'start'
      });
    },
    isTaskFinished(task) {
      return entityDataManager.isTaskFinished(task);
    },
    isTaskCompleted(task) {
      return task.status === TaskStatus.Completed;
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.task-title {
  width: calc(100% - 30px);
}

.task-title-content {
  width: 100%;
}

.task-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
}

:deep(.task-accordion) {
  .accordion-button {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
}

.auto-generated-task-icon {
  width: 22px;
  height: 22px;
}

.all-tasks-search-action {
  height: 59px;
}

// Mobile:
@media (max-width: $breaking-point) {
  label {
    width: 100%;
  }

  .col-width {
    width: 100%;
  }

  .all-tasks-search-action {
    height: auto;
    width: 100%;
  }
}
</style>
